import { FC, Fragment, useEffect, useState } from "react";
import { Container } from "./style";
import Pagination from "components/particles/pagination/pagination";
import Button from "components/particles/primary-button";
import AddWiseTransferModal from "components/popus/wise-transfers-popup/add-wise-transfer";
import edit from "assets/images/edit.svg";
import NoDataFound from "components/particles/no-data-found";
import { useSelector } from "react-redux";
import { dispatch, RootState } from "store";
import { setModalOpened } from "store/user-slice";
import { useWiseTransfers } from "./useHook";
import { StripeToWiseTransferDTO } from "utils/helpers/models/stripeToWiseTransfer.dto";
import { useForm } from "react-hook-form";

interface WiseTransferSearchDTO {
    startDate: string;
    endDate: string;
}

const WiseTransfers: FC = () => {
    const [data, setData] = useState<any[]>([]);
    const [dataById, setDataById] = useState<StripeToWiseTransferDTO>();
    const [paginationState, setPaginationState] = useState<any>({});
    const [id, setId] = useState<number>(0);
    const [searchFilters, setSearchFilters] = useState<WiseTransferSearchDTO>({
        startDate: "",
        endDate: "",
    });
    const { isLoading, wiseTransfersModalIsOpen } = useSelector(
        (state: RootState) => state.user
    );

    const { getTransfers, getTransferById } = useWiseTransfers();
    const {
        register,
        handleSubmit,
        setValue,
        reset: formReset,
        getValues,
    } = useForm<WiseTransferSearchDTO>();

    const limit = 10;

    const fetchNextRecords = (offset: number) => {
        const filters: any = {
            offset,
            limit,
        };

        // Only add date filters if both are provided
        if (searchFilters.startDate && searchFilters.endDate) {
            filters.startDate = searchFilters.startDate;
            filters.endDate = searchFilters.endDate;
        }

        getTransfers(
            setData,
            filters,
            (paginationState: any) => {
                setPaginationState(paginationState);
                const toTopElement = document.getElementById("toTop");
                if (toTopElement) {
                    toTopElement.scrollIntoView({ behavior: "smooth" });
                } else {
                    window.scrollTo(0, 0);
                }
            }
        );
    };

    const addTransfer = () => {
        setId(0);
        setDataById(undefined);
        dispatch(setModalOpened("WiseTransfers"));
    };

    const handleEdit = (transfer: StripeToWiseTransferDTO) => {
        setId(transfer.id);
        const amount = typeof transfer.amount === 'number'
            ? transfer.amount
            : typeof transfer.amount === 'string'
                ? parseFloat(transfer.amount)
                : 0;

        setDataById({
            id: transfer.id,
            amount: isNaN(amount) ? 0 : amount,
            date: transfer.date,
        });
        dispatch(setModalOpened("WiseTransfers"));
    };

    const handleUpdateInState = (updatedTransfer: StripeToWiseTransferDTO) => {
        if (!updatedTransfer || !updatedTransfer.id) {
            console.error("⚠️ Invalid updatedTransfer:", updatedTransfer);
            return;
        }
        setData((prev) =>
            prev.map((t) =>
                t.id === updatedTransfer.id ? { ...t, ...updatedTransfer } : t
            )
        );
    };

    const search = (filters: WiseTransferSearchDTO) => {
        // Only search if both dates are provided
        if (!filters.startDate || !filters.endDate) {
            return;
        }

        setSearchFilters(filters);
        getTransfers(
            setData,
            {
                offset: 0,
                limit,
                startDate: filters.startDate,
                endDate: filters.endDate,
            },
            setPaginationState
        );
    };

    const reset = () => {
        setValue("startDate", "");
        setValue("endDate", "");
        formReset();
        setSearchFilters({
            startDate: "",
            endDate: "",
        });
        getTransfers(setData, { offset: 0, limit }, setPaginationState);
    };

    useEffect(() => {
        getTransfers(setData, { offset: 0, limit }, setPaginationState);
    }, []);

    const refreshData = () => {
        const filters: any = {
            offset: 0,
            limit,
        };

        if (searchFilters.startDate && searchFilters.endDate) {
            filters.startDate = searchFilters.startDate;
            filters.endDate = searchFilters.endDate;
        }

        getTransfers(setData, filters, setPaginationState);
    };

    const columns: any = [
        {
            name: "Sr. No.",
            class: "",
        },
        {
            name: "Amount",
            class: "",
        },
        {
            name: "Date",
            class: "",
        },
        {
            name: "Actions",
            class: "th",
        },
    ];

    return (
        <Container id="toTop">
            <div className="title">
                <h1>Wise Transfers</h1>
                <Button
                    title="Add Transfer"
                    width="16rem"
                    fill="true"
                    backgroundcolor="var(--reset-button)"
                    onclick={addTransfer}
                />
            </div>

            <div className="search-container">
                <div className="input-fields">
                    <div className="custom-date-input-container">
                        <label htmlFor="startDate">Start Date</label>
                        <div className="date-input-wrapper">
                            <input
                                id="startDate"
                                type="date"
                                className="custom-date-input"
                                placeholder="Start Date"
                                max={new Date().toISOString().split('T')[0]}
                                {...register("startDate")}
                            />
                        </div>
                    </div>
                    <div className="custom-date-input-container">
                        <label htmlFor="endDate">End Date</label>
                        <div className="date-input-wrapper">
                            <input
                                id="endDate"
                                type="date"
                                className="custom-date-input"
                                placeholder="End Date"
                                max={new Date().toISOString().split('T')[0]}
                                {...register("endDate")}
                            />
                        </div>
                    </div>
                </div>
                <div className="button-fields">
                    <Button
                        title="Search"
                        width="16rem"
                        fill="true"
                        onclick={handleSubmit(search)}
                    />
                    <Button
                        title="Reset"
                        width="16rem"
                        fill="true"
                        backgroundcolor="var(--reset-button)"
                        onclick={handleSubmit(reset)}
                    />
                </div>
            </div>

            <div className="data-table">
                <table>
                    <thead>
                        <tr>
                            {columns.map((column: any, index: number) => {
                                return (
                                    <th className={column.class} key={index}>
                                        {column.name}
                                    </th>
                                );
                            })}
                        </tr>
                    </thead>
                    <tbody>
                        {data?.length > 0 ? (
                            data?.map((item: any, index: number) => {
                                const amount = typeof item.amount === 'number'
                                    ? item.amount
                                    : typeof item.amount === 'string'
                                        ? parseFloat(item.amount)
                                        : 0;
                                const formattedAmount = isNaN(amount) ? "0.00" : amount.toFixed(2);

                                let formattedDate = "-";
                                if (item.date) {
                                    try {
                                        const dateObj = new Date(item.date);
                                        if (!isNaN(dateObj.getTime())) {
                                            formattedDate = dateObj.toLocaleDateString("en-US", {
                                                year: "numeric",
                                                month: "2-digit",
                                                day: "2-digit",
                                            });
                                        }
                                    } catch (error) {
                                        console.error("Error formatting date:", error);
                                    }
                                }

                                return (
                                    <tr key={index}>
                                        <td>{index + 1}</td>
                                        <td>${formattedAmount}</td>
                                        <td>{formattedDate}</td>
                                        <td>
                                            <div
                                                className="view-container"
                                                onClick={() => handleEdit(item)}
                                            >
                                                <img src={edit} alt="edit" />
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })
                        ) : (
                            <NoDataFound isShow={!isLoading} />
                        )}
                    </tbody>
                </table>
            </div>

            {paginationState?.total > 0 && (
                <Fragment>
                    <Pagination
                        totalRecords={paginationState?.total || 0}
                        perPage={limit}
                        handleFetchNextRecords={fetchNextRecords}
                    />
                </Fragment>
            )}

            {wiseTransfersModalIsOpen ? (
                <AddWiseTransferModal
                    id={id}
                    data={dataById}
                    onSuccess={refreshData}
                    updateList={handleUpdateInState}
                />
            ) : (
                ""
            )}
        </Container>
    );
};

export default WiseTransfers;

